import React from 'react';
import styled, { css } from 'styled-components';
import { COLORS } from '../../../theme/Colors';

export type CardVariant = 'default' | 'elevated' | 'outlined' | 'interactive';

export interface CardProps {
  variant?: CardVariant;
  padding?: 'none' | 'small' | 'medium' | 'large';
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'medium',
  children,
  onClick,
  className,
  header,
  footer,
}) => {
  return (
    <StyledCard
      $variant={variant}
      $padding={padding}
      $clickable={!!onClick}
      onClick={onClick}
      className={className}
    >
      {header && <CardHeader>{header}</CardHeader>}
      <CardContent $padding={padding}>{children}</CardContent>
      {footer && <CardFooter>{footer}</CardFooter>}
    </StyledCard>
  );
};

// Styled Components
const paddingStyles = {
  none: '0',
  small: '12px',
  medium: '20px',
  large: '32px',
};

const variantStyles = {
  default: css`
    background-color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  `,
  elevated: css`
    background-color: white;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  `,
  outlined: css`
    background-color: white;
    border: 1px solid #E5E7EB;
    box-shadow: none;
  `,
  interactive: css`
    background-color: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease-in-out;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  `,
};

const StyledCard = styled.div<{
  $variant: CardVariant;
  $padding: string;
  $clickable: boolean;
}>`
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  
  ${({ $variant }) => variantStyles[$variant]}
  ${({ $clickable }) => $clickable && css`
    cursor: pointer;
  `}
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #E5E7EB;
  background-color: #F9FAFB;
`;

const CardContent = styled.div<{ $padding: string }>`
  padding: ${({ $padding }) => paddingStyles[$padding]};
`;

const CardFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #E5E7EB;
  background-color: #F9FAFB;
`;

export default Card;